/**
 * CSV Parser Utility
 * Handles CSV file parsing and data extraction
 */

import { EmployeeRecord } from './employee-indexeddb';

export interface CsvParseResult {
  headers: string[];
  rows: string[][];
  data: Record<string, string>[];
}

export type CsvPreview = Pick<CsvParseResult, 'headers' | 'rows'> & { totalRows: number };

export interface CsvColumnMapping {
  csvColumn: string;
  targetField: keyof EmployeeRecord;
  transform?: (value: string) => string;
}

/**
 * Parse CSV content into structured data
 */
export function parseCSV(content: string): CsvParseResult {
  const lines = parseCsvRecords(content.replace(/^\uFEFF/, ''));
  
  if (lines.length === 0) {
    throw new Error('CSV file is empty');
  }
  
  // Parse headers
  const headers = lines[0].map(header => header.trim());
  
  // Parse rows
  const rows: string[][] = [];
  const data: Record<string, string>[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const row = lines[i];
    
    // Skip empty rows
    if (row.length === 0 || row.every(cell => !cell.trim())) {
      continue;
    }
    
    // Ensure row has same number of columns as headers
    while (row.length < headers.length) {
      row.push('');
    }
    
    rows.push(row);
    
    // Create object from row
    const rowData: Record<string, string> = {};
    headers.forEach((header, index) => {
      rowData[header] = row[index] || '';
    });
    data.push(rowData);
  }
  
  return { headers, rows, data };
}

/**
 * Parse a single CSV line handling quoted values
 */
function parseCsvRecords(content: string): string[][] {
  const result: string[][] = [];
  let row: string[] = [];
  let current = '';
  let inQuotes = false;
  let closedQuote = false;
  let quotedField = false;
  const finishField = () => {
    row.push(quotedField ? current : current.trim());
    current = '';
    closedQuote = false;
    quotedField = false;
  };
  const finishRow = () => {
    finishField();
    if (row.some(cell => cell.trim())) result.push(row);
    row = [];
  };

  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    const nextChar = content[i + 1];
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else if (inQuotes) {
        inQuotes = false;
        closedQuote = true;
      } else if (!closedQuote && !current.trim()) {
        current = '';
        inQuotes = true;
        quotedField = true;
      } else {
        throw new Error('Invalid quote in CSV field');
      }
    } else if (inQuotes) {
      current += char;
    } else if (char === ',') {
      finishField();
    } else if (char === '\n' || char === '\r') {
      finishRow();
      if (char === '\r' && nextChar === '\n') i++;
    } else if (closedQuote) {
      if (!/\s/.test(char)) throw new Error('Unexpected text after quoted CSV field');
    } else {
      current += char;
    }
  }
  if (inQuotes) throw new Error('Unclosed quote in CSV field');
  finishRow();
  return result;
}

/**
 * Auto-detect column mappings based on common patterns
 */
export function autoDetectMappings(headers: string[]): CsvColumnMapping[] {
  const mappings: CsvColumnMapping[] = [];
  
  const patterns: Array<{
    patterns: RegExp[];
    field: keyof EmployeeRecord;
    transform?: (value: string) => string;
  }> = [
    {
      patterns: [/社員番号/i, /employee\s*id/i, /emp\s*id/i, /^id$/i],
      field: 'employeeId'
    },
    {
      patterns: [/^氏名$/i, /^名前$/i, /^name$/i, /full\s*name/i],
      field: 'name'
    },
    {
      patterns: [/氏名カナ/i, /氏名\s*かな/i, /name\s*kana/i, /フリガナ/i],
      field: 'nameKana'
    },
    {
      patterns: [/役職/i, /職位/i, /title/i, /position/i, /role/i],
      field: 'role'
    },
    {
      patterns: [/メール/i, /email/i, /mail/i, /e-mail/i],
      field: 'email'
    },
    {
      patterns: [/携帯/i, /電話/i, /phone/i, /mobile/i, /tel/i],
      field: 'phone'
    },
    {
      patterns: [/所属/i, /部署/i, /部門/i, /^本部$/i, /department/i, /dept/i, /division/i],
      field: 'department'
    },
    {
      patterns: [/入社日/i, /入社年月日/i, /join\s*date/i, /hire\s*date/i, /start\s*date/i],
      field: 'joinDate',
      transform: (value: string) => {
        // Convert various date formats to ISO format
        if (!value) return '';
        
        // Try to parse Japanese date format (YYYY/MM/DD)
        const jpMatch = value.match(/(\d{4})[/年](\d{1,2})[/月](\d{1,2})/);
        if (jpMatch) {
          const [, year, month, day] = jpMatch;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        // Try to parse US date format (MM/DD/YYYY)
        const usMatch = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (usMatch) {
          const [, month, day, year] = usMatch;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        // Return as-is if already in ISO format or unparseable
        return value;
      }
    }
  ];
  
  headers.forEach(header => {
    for (const pattern of patterns) {
      if (pattern.patterns.some(regex => regex.test(header))) {
        mappings.push({
          csvColumn: header,
          targetField: pattern.field,
          transform: pattern.transform
        });
        break;
      }
    }
  });
  
  // Ensure email field is mapped (required)
  const hasEmail = mappings.some(m => m.targetField === 'email');
  if (!hasEmail && headers.length > 0) {
    console.warn('No email column detected, using first column as fallback');
    mappings.push({
      csvColumn: headers[0],
      targetField: 'email'
    });
  }
  
  return mappings;
}

/**
 * Convert CSV data to employee records using mappings
 */
export function csvToEmployeeRecords(
  data: Record<string, string>[],
  mappings: CsvColumnMapping[]
): EmployeeRecord[] {
  const records: EmployeeRecord[] = [];
  
  for (const row of data) {
    const record: Partial<EmployeeRecord> = {
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    };
    
    for (const mapping of mappings) {
      const value = row[mapping.csvColumn];
      if (value !== undefined && value !== '') {
        const trimmedValue = typeof value === 'string' ? value.trim() : value;
        const transformedValue = mapping.transform ? mapping.transform(trimmedValue) : trimmedValue;
        Object.assign(record, { [mapping.targetField]: typeof transformedValue === 'string'
          ? transformedValue.trim()
          : transformedValue });
      }
    }
    
    // Only add if email is present (required field)
    if (record.email) {
      records.push(record as EmployeeRecord);
    }
  }
  
  return records;
}

/**
 * Validate CSV data
 */
export function validateCsvData(data: CsvParseResult): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Check if data exists
  if (!data.data || data.data.length === 0) {
    errors.push('No data found in CSV file');
    return { isValid: false, errors, warnings };
  }
  
  // Check for headers
  if (!data.headers || data.headers.length === 0) {
    errors.push('No headers found in CSV file');
    return { isValid: false, errors, warnings };
  }
  
  // Check for duplicate headers
  const headerSet = new Set<string>();
  data.headers.forEach(header => {
    if (headerSet.has(header)) {
      warnings.push(`Duplicate header found: "${header}"`);
    }
    headerSet.add(header);
  });
  
  // Check for email column
  const mappings = autoDetectMappings(data.headers);
  const hasEmail = mappings.some(m => m.targetField === 'email');
  if (!hasEmail) {
    warnings.push('No email column detected. Please map email column manually.');
  }
  
  // Check for reasonable number of columns
  if (data.headers.length > 50) {
    warnings.push(`Large number of columns detected (${data.headers.length}). This may impact performance.`);
  }
  
  // Check for reasonable number of rows
  if (data.data.length > 10000) {
    warnings.push(`Large number of rows detected (${data.data.length}). Import may take some time.`);
  }
  
  // Check for empty rows
  const emptyRows = data.data.filter(row => 
    Object.values(row).every(value => !value || !value.trim())
  ).length;
  if (emptyRows > 0) {
    warnings.push(`${emptyRows} empty row(s) will be skipped.`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Read CSV file
 */
export function readCsvFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const content = event.target?.result as string;
      resolve(content);
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      reject(new Error('File size exceeds 10MB limit'));
      return;
    }
    
    // Check file type
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      reject(new Error('File must be a CSV file'));
      return;
    }
    
    reader.readAsText(file, 'UTF-8');
  });
}

/**
 * Generate sample CSV template
 */
export function generateCsvTemplate(): string {
  const headers = [
    '社員番号',
    '氏名',
    '氏名カナ',
    '役職',
    'メールアドレス',
    '社用携帯',
    '所属',
    '入社日'
  ];
  
  const sampleData = [
    ['0001', '山田太郎', 'ヤマダタロウ', '部長', 'taro.yamada@system-exe.jp', '080-1234-5678', '開発部', '2020/04/01'],
    ['0002', '佐藤花子', 'サトウハナコ', '課長', 'hanako.sato@system-exe.jp', '080-2345-6789', '営業部', '2019/10/01'],
    ['0003', '鈴木一郎', 'スズキイチロウ', 'エンジニア', 'ichiro.suzuki@system-exe.jp', '080-3456-7890', '開発部', '2021/04/01']
  ];
  
  const csv = [
    headers.join(','),
    ...sampleData.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');
  
  return csv;
}

/**
 * Export data to CSV
 */
export function exportToCsv(data: unknown[], filename = 'export.csv'): void {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }
  
  // Get headers from first object
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape quotes and wrap in quotes if contains comma or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value || '';
      }).join(',')
    )
  ].join('\n');
  
  // Create blob and download
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}
